import {type ComponentProps, useCallback, useEffect, useRef} from 'react';
import {X} from 'lucide-react';
import {Input as BaseInput} from '@ui/shadcn/input';

/**
 * Обёртка над shadcn Input с кнопкой очистки (иконка X).
 * Не вызывает ре-рендеров при вводе — кнопка управляется через DOM.
 */
export const Input = ({
  className,
  type,
  ...props
}: ComponentProps<typeof BaseInput>) => {
  // type="search" — браузер сам рисует крестик, свой не нужен.
  const isSearch = type === 'search';
  const inputRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const sync = useCallback(() => {
    const el = inputRef.current;
    const btn = btnRef.current;
    if (!el || !btn || isSearch) return;
    const show = el.value.length > 0 && !props.readOnly && !props.disabled;
    btn.style.display = show ? '' : 'none';
    el.style.paddingRight = show ? '1.75rem' : '';
  }, [isSearch, props.readOnly, props.disabled]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    sync();
    el.addEventListener('input', sync);
    return () => el.removeEventListener('input', sync);
  }, [sync]);

  const handleClear = () => {
    const el = inputRef.current;
    if (!el || el.readOnly || el.disabled) return;
    const setter = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      'value',
    )?.set?.bind(el);
    if (setter) {
      setter('');
      el.dispatchEvent(new Event('input', {bubbles: true}));
    }
    sync();
    el.focus();
  };

  return (
    <div className='relative w-full flex items-center'>
      <BaseInput ref={inputRef} type={type} className={className} {...props} />
      {!isSearch && (
        <button
          ref={btnRef}
          type='button'
          onClick={handleClear}
          disabled={props.disabled || props.readOnly}
          tabIndex={-1}
          aria-label='Очистить'
          style={{display: 'none'}}
          className='absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors'
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
};
